/**
 * RESSO config (resso.json) typed re-export.
 *
 * Schwager-derived Codelco audit taxonomy: ~50 documental items + ~30 terreno items
 * organized in jerarquía secciones → subsecciones → items.
 *
 * Both server (scoring.service) and client (display) import this from @sime/shared.
 */

import ressoConfigData from '../config/resso.json' with { type: 'json' };
import type { HallazgosConfig, BonusRule } from './resso-scoring.js';

export interface RessoCriterioNumeric {
  value: number;
  label: string;
  descripcion?: string;
  type?: never;
}

export interface RessoCriterioNA {
  value: null;
  label: string;
  type: 'NA';
}

export type RessoCriterio = RessoCriterioNumeric | RessoCriterioNA;

export interface RessoDocumentalItem {
  id: string;
  text: string;
  pond?: number;
  tipo?: string;
  materias?: string[];
  objetivos?: string[];
  criterios?: RessoCriterio[];
  naPolicy?: { allowNA?: boolean; [key: string]: unknown };
  [key: string]: unknown;
}

export interface RessoDocumentalSubseccion {
  titulo: string;
  items: RessoDocumentalItem[];
}

export interface RessoDocumentalSeccion {
  id: string;
  titulo: string;
  subsecciones: RessoDocumentalSubseccion[];
}

export interface RessoTerrenoItem {
  id: string;
  text: string;
  pond?: number;
  [key: string]: unknown;
}

export interface RessoTerrenoSeccion {
  titulo: string;
  items: RessoTerrenoItem[];
}

export interface RessoConfigShape {
  schema?: { name: string; version: string };
  documental: RessoDocumentalSeccion[];
  terreno: RessoTerrenoSeccion[];
  /** Hallazgos categorias + tiers; consumed by client-side preview formula. */
  hallazgos?: HallazgosConfig;
  /** Bonus rules; consumed by client-side preview formula. */
  bonusRules?: BonusRule[];
  /** Feature flags — gates UI like hallazgos.enabled. */
  features?: {
    hallazgos?: { enabled: boolean };
    [key: string]: unknown;
  };
  [key: string]: unknown;
}

export const ressoConfig: RessoConfigShape = ressoConfigData as unknown as RessoConfigShape;

/**
 * Flatten all documental items across secciones+subsecciones.
 */
export function flattenDocumentalItems(config: RessoConfigShape): RessoDocumentalItem[] {
  return config.documental.flatMap((s) => s.subsecciones.flatMap((sub) => sub.items));
}

/**
 * Flatten all terreno items across secciones.
 */
export function flattenTerrenoItems(config: RessoConfigShape): RessoTerrenoItem[] {
  return config.terreno.flatMap((s) => s.items);
}
